import React, { useState } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { Button } from '../ui/button';
import { Download } from 'lucide-react';
import { CurveChartProps } from '@/types/chat';
import {
  ADP_EVENTS,
  getBernoulliAdp,
  trackAdpWaypoint,
} from '@/services/analytics/adp';

export const CurveChart = ({ config, onAction }: CurveChartProps) => {
  const [isActionLoading, setIsActionLoading] = useState(false);

  const chartOptions = (config as Highcharts.Options) || {
    title: { text: 'No data' },
    series: [],
    credits: { enabled: false },
  };

  const handleAction = async (action: string, data: unknown) => {
    if (!onAction) return;
    setIsActionLoading(true);
    if (action === 'export_data') {
      trackAdpWaypoint(getBernoulliAdp(), ADP_EVENTS.CHART_EXPORT_DATA, {
        format: (data as { format?: string })?.format ?? 'unknown',
      });
    }
    try {
      await onAction(action, data);
    } catch (error) {
      console.error('Action failed:', error);
    } finally {
      setIsActionLoading(false);
    }
  };

  return (
    <div className="rounded-2xl border border-slate-300 overflow-hidden">
      <div className="p-6">
        <HighchartsReact highcharts={Highcharts} options={chartOptions} />
      </div>

      {onAction && (
        <div className="flex flex-wrap gap-2 px-6 py-4 bg-slate-50 border-t border-slate-300">
          <Button
            size="sm"
            variant="outline"
            onClick={() => handleAction('export_data', { format: 'csv' })}
            disabled={isActionLoading}
            className="rounded-lg hover:bg-blue-50 hover:border-blue-500 transition-colors">
            <Download className="h-3.5 w-3.5 mr-1.5" />
            Export Data
          </Button>
        </div>
      )}

      {config && (
        <details className="border-t border-slate-300">
          <summary className="cursor-pointer px-6 py-3 text-sm text-slate-600 hover:text-slate-900 hover:bg-slate-50 transition-colors">
            View raw config
          </summary>
          <div className="px-6 py-4 bg-slate-50">
            <pre className="text-xs bg-white p-4 rounded-lg overflow-auto max-h-60 border border-slate-300 text-slate-700">
              {JSON.stringify(config, null, 2)}
            </pre>
          </div>
        </details>
      )}
    </div>
  );
};
